import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast, Toaster } from "sonner";
import { Info } from "lucide-react";

const LABELS = {
  CASH_TOLERANCE: "Cash tolerance ₹",
  LOCK_HOURS: "Lock hours after shift end",
  DEPOSIT_SLA_MIN: "Deposit SLA (min)",
  PROOF_GRACE_HOURS: "Proof grace (hours)",
  SHORTAGE_TIERS: "Shortage tiers (JSON)",
  SALES_DROP_PCT: "Sales drop %",
  RECON_TOLERANCE: "Reconciliation tolerance ₹",
  OCR_ENABLED: "OCR enabled (boolean)",
  OCR_CONF_THRESHOLD: "OCR confidence threshold",
  SHIFT_MODE: "Shift mode (3-shift / 2-shift)",
  ADJUSTMENT_ABUSE_N: "Adjustment abuse count",
  ESCALATION_HOURS: "Escalate open flag after (hours)",
  ESCALATION_MIN_IMPACT: "Escalation min ₹ impact",
  ESCALATION_EMAILS: "Escalation owner emails (JSON array)",
  FUEL_PO_SHORT_TOLERANCE_L: "PO delivery — flag if short by (L)",
  FUEL_PO_SHORT_HIGH_L: "PO delivery — HIGH severity above (L)",
  FUEL_PO_INVOICE_TOLERANCE: "PO invoice — flag variance above (₹)",
  FUEL_PO_INVOICE_HIGH: "PO invoice — HIGH severity above (₹)",
  // Phase-3 B4 — payroll multipliers.
  OT_MULTIPLIER: "Overtime multiplier (× rate)",
  WEEKLY_OFF_MULTIPLIER: "Weekly-off multiplier (× daily rate)",
  WEEKLY_OFF_RULE_ENABLED: "Weekly-off premium enabled (boolean)",
  // Phase-3 C4 — debtor ageing + cost of funds.
  AGEING_BUCKETS_DAYS: "Debtor ageing buckets (JSON day cutoffs)",
  COST_OF_FUNDS_PCT: "Cost of funds % (interest cost)",
};

const BOOL_KEYS = ["OCR_ENABLED", "WEEKLY_OFF_RULE_ENABLED"];
const SHIFT_MODE_OPTIONS = ["3-shift", "2-shift"];
const NUMBER_KEYS = [
  "CASH_TOLERANCE", "LOCK_HOURS", "DEPOSIT_SLA_MIN", "PROOF_GRACE_HOURS",
  "SALES_DROP_PCT", "RECON_TOLERANCE", "OCR_CONF_THRESHOLD",
  "ADJUSTMENT_ABUSE_N", "ESCALATION_HOURS", "ESCALATION_MIN_IMPACT",
  "FUEL_PO_SHORT_TOLERANCE_L", "FUEL_PO_SHORT_HIGH_L",
  "FUEL_PO_INVOICE_TOLERANCE", "FUEL_PO_INVOICE_HIGH",
  "OT_MULTIPLIER", "WEEKLY_OFF_MULTIPLIER", "COST_OF_FUNDS_PCT",
];

export default function Config() {
  const [cfg, setCfg] = useState({});
  const [docs, setDocs] = useState({});

  const load = async () => {
    const [c, d] = await Promise.all([
      api.get("/masters/config"),
      api.get("/masters/config/docs").catch(() => ({ data: {} })),
    ]);
    setCfg(c.data);
    setDocs(d.data || {});
  };
  useEffect(() => { load(); }, []);

  const save = async (k, v) => {
    try {
      let value = v;
      if (k === "SHORTAGE_TIERS" && typeof v === "string") value = JSON.parse(v);
      if (k === "ESCALATION_EMAILS" && typeof v === "string") value = JSON.parse(v);
      if (BOOL_KEYS.includes(k)) value = v === "true" || v === true;
      if (NUMBER_KEYS.includes(k)) value = Number(v);
      await api.post("/masters/config", { key: k, value });
      toast.success("Saved " + k);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-4" data-testid="config-root">
        <Toaster position="top-center" />
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Global Config</h1>
          <p className="text-sm text-slate-600 mt-1">
            Every knob has a hover tip explaining what it controls. Only owner / developer / admin can change these.
          </p>
        </div>

        <Card className="p-3 border-2 border-slate-300 rounded-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.entries(LABELS).map(([k, label]) => {
              const desc = docs[k];
              const isBool = BOOL_KEYS.includes(k);
              const isShiftMode = k === "SHIFT_MODE";
              return (
                <div key={k}>
                  <div className="flex items-center gap-1">
                    <label
                      className="text-xs font-bold uppercase tracking-wider text-slate-600"
                      htmlFor={`cfg-${k}`}
                    >
                      {label}
                    </label>
                    {desc && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button type="button" data-testid={`cfg-help-${k}`} className="text-slate-400 hover:text-slate-700">
                            <Info size={12} />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-xs text-xs leading-snug">
                          <div className="font-semibold">{desc.unit && `(${desc.unit})`}</div>
                          <div>{desc.text}</div>
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                  <div className="flex gap-2 mt-1">
                    {isBool ? (
                      <div className="flex gap-1 flex-1">
                        {[
                          { v: true, label: "On" },
                          { v: false, label: "Off" },
                        ].map((opt) => (
                          <button
                            key={String(opt.v)}
                            type="button"
                            data-testid={`cfg-${k}-${opt.v ? "on" : "off"}`}
                            onClick={() => save(k, opt.v)}
                            className={
                              "flex-1 h-10 border-2 font-bold text-sm rounded-sm " +
                              (Boolean(cfg[k]) === opt.v
                                ? "bg-slate-900 text-white border-slate-900"
                                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50")
                            }
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    ) : isShiftMode ? (
                      <div className="flex gap-1 flex-1">
                        {SHIFT_MODE_OPTIONS.map((opt) => (
                          <button
                            key={opt}
                            type="button"
                            data-testid={`cfg-${k}-${opt}`}
                            onClick={() => save(k, opt)}
                            className={
                              "flex-1 h-10 border-2 font-bold text-sm rounded-sm " +
                              (cfg[k] === opt
                                ? "bg-slate-900 text-white border-slate-900"
                                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50")
                            }
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <>
                        <Input
                          id={`cfg-${k}`}
                          data-testid={`cfg-${k}`}
                          value={typeof cfg[k] === "object" ? JSON.stringify(cfg[k]) : String(cfg[k] ?? "")}
                          onChange={(e) => setCfg({ ...cfg, [k]: e.target.value })}
                          className="h-10 border border-slate-300 flex-1"
                        />
                        <Button
                          size="sm"
                          onClick={() => save(k, cfg[k])}
                          className="bg-slate-900 hover:bg-slate-800 text-white"
                        >
                          Save
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        <Card className="p-3 border-2 border-slate-300 rounded-sm">
          <div className="text-xs text-slate-500">
            Tip: A big <em>SALES_DROP_PCT</em> alert almost always turns out to be a rate change or a
            slow day — check the fuel-price history on the Masters page before assuming shortage.
          </div>
        </Card>
      </div>
    </TooltipProvider>
  );
}
